import Settings from "Data/Settings";
import { Sine } from "gsap";
import { TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import { BaseCaptionRenderer } from "./BaseCaptionRenderer";

export class DefaultCaptionRenderer extends BaseCaptionRenderer {

    protected _activeLine?: string;
    private readonly _bg: PIXI.Sprite;
    private readonly _text: PIXI.BitmapText;
    private _textTween: TweenLite | undefined;
    private _bgTween: TweenLite | undefined;

    private readonly _fontSize: number;

    constructor(pFontName: string, pFontSize: number = 20) {
        super();

        this._bg = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._bg.anchor.set(0.5, 0);
        this._bg.height = 60;
        this._bg.tint = 0x000000;
        this._bg.alpha = 0;
        this.addChild(this._bg);

        this._fontSize = pFontSize;

        this._text = new PIXI.BitmapText("", {
            align: "center",
            fontName: pFontName,
            fontSize: pFontSize * Settings.textSizeMultiplier,
        });
        this._text.alpha = 0;
        this._text.visible = false;
        this.addChild(this._text);

        PubSub.subscribe(Topics.SETTINGS_CHANGED, ()=>{
            this._text.fontSize = this._fontSize * Settings.textSizeMultiplier;
            this.onResize(this._size);
        });
    }

    public start(templateVariables: SpringRoll.TemplateVariables): void {
        this._text.visible = true;
        this._bg.visible = true;
        this.killTween(this._textTween);
        this.killTween(this._bgTween);
        this._textTween = new TweenLite(this._text, 0.25, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        this._bgTween = new TweenLite(this._bg, 0.15, {
            alpha: 0.6,
            ease: Sine.easeInOut,
        });
        super.start(templateVariables);
    }

    public stop(): void {
        this.killTween(this._textTween);
        this.killTween(this._bgTween);
        this._textTween = new TweenLite(this._text, 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
            onComplete: () => {
                this._text.visible = false;
            },
        });
        this._bgTween = new TweenLite(this._bg, 0.25, {
            delay: 0.25,
            alpha: 0,
            ease: Sine.easeInOut,
            onComplete: () => {
                this._bg.visible = false;
            },
        });
        this._activeLine = undefined;
        super.stop();
    }

    public lineBegin(pLine: SpringRoll.Timedline): void {
        super.lineBegin(pLine);
        if (this._activeLine) {
            if (pLine.content !== this._activeLine) {
                console.warn("SKIPPING CAPTION", pLine.content);
            }
            return;
        }

        this._activeLine = pLine.content;

        let text = pLine.content;

        if (this.templateVariables !== undefined) {
            const keys = Object.keys(this.templateVariables);
            for (let i = 0; i < keys.length; i++) {
                // replace variables of format <FOO>
                text = text.split("<" + keys[i].toUpperCase() + ">").join(this.templateVariables[keys[i]]);
            }
        }

        // pLine.content = pLine.content.replace(/<[^>]*>/ig, "");
        this._text.text = text;
        this.onResize(this._size);
    }

    public lineEnd(): void {
        super.lineEnd();
        this._activeLine = undefined;
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
        this._bg.width = this._size.x;
        this._bg.position.set(this._size.x * 0.5, 0);

        this._text.maxWidth = (this._size.x * 2) - 240; // pbs games app close button is 60px wide
        this._text.scale.set(0.5);
        this._text.position.set(
            (this._size.x - this._text.width) * 0.5,
            (this._bg.height - this._text.height) * 0.5,
        );
    }

    private killTween(pTween: TweenLite | undefined): void {
        if (pTween !== undefined) {
            pTween.kill();
        }
    }
}
